import { LegalBasisChip, compareLegalBasisByArticle } from '../../../utils/legalBasisFormatter';
import type { RefinedLegalBasis } from '@safetyhub/shared';
import { Button } from '@safetyhub/ui';

type LegalBasis = RefinedLegalBasis;
type ChangeStatus = 'unchanged' | 'added' | 'removed';

interface LegalChangeInfo {
  value: LegalBasis;
  status: ChangeStatus;
}

interface LegalGroupListProps {
  legalBasis: LegalBasis[];
  changes?: LegalChangeInfo[];
  isAdding: boolean;
  disabled: boolean;
  removingIndex: number | null;
  onStartAdd: () => void;
  onRemove: (index: number) => void;
}

/**
 * 관련법규 목록을 type별 그룹으로 렌더링하는 컴포넌트.
 * EditableLegalList 내부의 IIFE 렌더 로직을 추출.
 */
export function LegalGroupList({
  legalBasis,
  changes,
  isAdding,
  disabled,
  removingIndex,
  onStartAdd,
  onRemove,
}: LegalGroupListProps) {
  const itemsToRender = changes || legalBasis.map((law) => ({ value: law, status: 'unchanged' as ChangeStatus }));
  const hasContent = itemsToRender.length > 0;

  if (!hasContent) {
    return !isAdding ? (
      <div className="text-xs text-ink-400 italic">
        관련 법규가 없습니다.
        {!disabled && (
          <Button
            variant="primary"
            size="xs"
            className="ml-1"
            onClick={onStartAdd}
          >
            추가하기
          </Button>
        )}
      </div>
    ) : null;
  }

  // legalIdx는 itemsToRender 순서(정렬 이전) 기반 — legalBasis 배열의 원본 인덱스로
  // onRemove(legalIdx)에 정확한 위치를 전달하기 위함. 그룹 정렬은 표시 순서만 영향.
  let lbIdx = 0;
  const annotatedItems = itemsToRender.map((item, flatIdx) => ({
    law: item.value,
    status: item.status,
    flatIdx,
    legalIdx: item.status !== 'removed' ? lbIdx++ : -1,
  }));

  // Group by type (preserving annotation)
  const groups: { type: string; items: typeof annotatedItems }[] = [];
  const typeMap = new Map<string, number>();
  for (const item of annotatedItems) {
    const type = item.law.type;
    const idx = typeMap.get(type);
    if (idx !== undefined) {
      groups[idx].items.push(item);
    } else {
      typeMap.set(type, groups.length);
      groups.push({ type, items: [item] });
    }
  }

  // 그룹별 조번호 + 호번호 오름차순 정렬 (legalIdx는 보존되어 onRemove 인덱스 정확)
  for (const group of groups) {
    group.items.sort((a, b) => compareLegalBasisByArticle(a.law, b.law));
  }

  return (
    <div className="space-y-2">
      {groups.map((group) => (
        <div key={group.type} className="text-xs rounded-card-sm border bg-brand-50 border-brand-100">
          <div className="font-medium text-brand-800 px-2 pt-2 pb-1">{group.type || '기타'}</div>
          <div className="space-y-1 px-2 pb-2">
            {group.items.map(({ law, status, flatIdx, legalIdx }) => {
              const canRemove = !disabled && status !== 'removed' && legalIdx !== -1;
              return (
                <LegalBasisChip
                  key={`l-${flatIdx}`}
                  law={law}
                  status={status}
                  onRemove={canRemove ? () => onRemove(legalIdx) : undefined}
                  isRemoving={removingIndex === legalIdx}
                  omitType
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
